import {FormSearchQuery as CastorFormSearchQuery} from '@aipb/gulliver/castor/model';
import {SearchRepository, SearchService} from '@aipb/gulliver/catalog/data-access';
import {CatalogEnvService} from '@aipb/gulliver/catalog/env';
import {SearchFormMode} from '@aipb/gulliver/catalog/model';
import {CatalogRouting} from '@aipb/gulliver/catalog/routing';
import {CommonModule} from '@angular/common';
import {Component, inject, Input, OnDestroy, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {TranslocoPipe} from '@jsverse/transloco';
import {SearchFormModeButtonComponent} from '../search-form-mode-button/search-form-mode-button.component';
import {MatIconModule} from '@angular/material/icon';
import {Overlay, OverlayModule} from '@angular/cdk/overlay';
import {SearchKeyboardService} from '../../services/search-keyboard.service';
import Keyboard from 'simple-keyboard';
import {
  VirtualKeyboardDialogComponent,
  VirtualKeyboardDialogDataModel,
  VirtualKeyboardDialogService
} from "@aipb/gulliver/virtual-keyboard";
import {DialogRef} from "@angular/cdk/dialog";

type RouteTarget = 'search-result' | 'detail';

@Component({
  selector: 'cat-search-form-simple',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TranslocoPipe,
    SearchFormModeButtonComponent,
    MatCardModule,
    MatIconModule,
    OverlayModule
  ],
  templateUrl: './search-form-simple.component.html',
  styleUrls: ['./search-form-simple.component.scss'],
  providers: [SearchKeyboardService]
})
export class SearchFormSimpleComponent implements OnDestroy {
  @Input() routeTarget: RouteTarget = 'search-result';
  @Input() routeToSearchAfterModeChange = true;

  catalogRouting = inject(CatalogRouting);
  catalogEnv = inject(CatalogEnvService);
  searchService = inject(SearchService);
  searchRepository = inject(SearchRepository);
  searchFormMode$ = this.searchRepository.searchFormMode$;
  keyboardOpen = signal(false);
  private fb = inject(FormBuilder);
  form: FormGroup = this.fb.group({
    query: ['']
  });

  private overlay = inject(Overlay);
  private keyboard: Keyboard;
  private searchKeyboardService = inject(SearchKeyboardService);

  private _virtualKeyboardRef: DialogRef<VirtualKeyboardDialogDataModel, VirtualKeyboardDialogComponent>;
  private virtualKeyboardDialog = inject(VirtualKeyboardDialogService);

  constructor() {
  }

  get hasVirtualKeyboard(): boolean {
    return !!this.catalogEnv.env.virtualKeyboard;
  }

  onSearch() {
    if (this.form.invalid) return;

    const query: CastorFormSearchQuery[] = [
      {
        fieldId: this.catalogEnv.env.castor.defaultFieldId,
        queryRow: this.form.value['query'],
        logOp: ''
      }
    ];
    this.searchService.addTab(
      {
        solrSearchParams: {
          query: query
        },
        settings: {
          asterRight: true,
          asciiSearch: true
        }
      },
      true
    );

    if (this.routeTarget === 'search-result') {
      this.catalogRouting.searchResult();
    } else if (this.routeTarget === 'detail') {
      this.catalogRouting.detailDefault();
    }
  }

  onModeChange(mode: SearchFormMode) {
    if (mode === 'advanced' && this.routeToSearchAfterModeChange) {
      this.catalogRouting.searchResult();
    }
  }

  onKeyboardOpenToggle() {
    this.virtualKeyboardDialog.open({
      keyboard: this.catalogEnv.env.virtualKeyboard,
      initialValue: this.form.value['query'],
      onChange: (input) => {
        this.form.patchValue({query: input})
      }
    }).then(r => {
      this._virtualKeyboardRef = r;
    })
  }

  onInputChange = (event: any) => {
    if (!this._virtualKeyboardRef) return;

    this._virtualKeyboardRef.componentRef.instance.setValue(event.target.value);
  };

  onClearInput() {
    this.form.patchValue({query: ''});

    if (this._virtualKeyboardRef) {
      this._virtualKeyboardRef.componentRef.instance.clearInput();
    }
  }

  ngOnDestroy() {
    if (!this._virtualKeyboardRef) return;

    this._virtualKeyboardRef.close();
  }
}
